<script setup lang="ts">
import type { ProviderStatus } from '../../providers/ProviderStatusPanel.vue'

import { FieldInput } from '@proj-airi/ui'
import { useDark, useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import Alert from '../../../misc/Alert.vue'
import ProviderStatusPanel from '../../providers/ProviderStatusPanel.vue'
import StepAppearance from './steps/StepAppearance.vue'
import StepBaiduSync from './steps/StepBaiduSync.vue'
import StepInstallPwa from './steps/StepInstallPwa.vue'
import StepMarketplace from './steps/StepMarketplace.vue'
import StepPersona from './steps/StepPersona.vue'
import StepRelationship from './steps/StepRelationship.vue'

import { useConsciousnessStore } from '../../../../stores/modules/consciousness'
import { useProvidersStore } from '../../../../stores/providers'
import { DISPLAY_MODEL_NONE, useSettings } from '../../../../stores/settings'
import { useUserPersonaStore } from '../../../../stores/user-persona'
import { serializeProviderError } from '../../../../utils/provider-error'
import { Callout } from '../../../layouts'
import { RadioCardDetail, RadioCardManySelect } from '../../../menu'
import { Button } from '../../../misc'
import { ProviderAccountIdInput } from '../../../scenarios/providers'

interface Emits {
  (e: 'configured'): void
  (e: 'skipped'): void
}

const emit = defineEmits<Emits>()

const TOTAL_STEPS = 10
const debounceTime = 500

const step = ref(1)
const direction = ref<'next' | 'previous'>('next')
const contentReady = ref(true)

provide('onboarding-content-ready', contentReady)

function onAfterEnter() {
  contentReady.value = true
}

function onBeforeLeave() {
  contentReady.value = false
}

const { t } = useI18n()
const providersStore = useProvidersStore()
const { providers, allChatProvidersMetadata } = storeToRefs(providersStore)
const consciousnessStore = useConsciousnessStore()
const {
  activeModel,
  modelSearchQuery,
  providerModels,
  isLoadingActiveProviderModels,
} = storeToRefs(consciousnessStore)
const settingsStore = useSettings()
const userPersonaStore = useUserPersonaStore()

const isDark = useDark()
const onboardingLogo = computed(() => isDark.value ? '/logo-dark.png' : '/logo.png')

// Live2D 偏好
const useLive2d = ref('yes')

// Provider 相关
const popularProviders = computed(() => {
  const popular = ['neko-teaparty', 'openai', 'anthropic', 'google-generative-ai', 'openrouter-ai', 'ollama', 'deepseek', 'player2', 'openai-compatible']
  return allChatProvidersMetadata.value
    .filter(provider => popular.includes(provider.id))
    .sort((a, b) => popular.indexOf(a.id) - popular.indexOf(b.id))
})

const selectedProviderId = ref('')
const apiKey = ref('')
const baseUrl = ref('')
const accountId = ref('')

const selectedProvider = computed(() => {
  return allChatProvidersMetadata.value.find(p => p.id === selectedProviderId.value) || null
})

const isValidating = ref(0)
const isValid = ref(false)
const validationMessage = ref('')

const needsApiKey = computed(() => {
  if (!selectedProvider.value)
    return false
  return selectedProvider.value.id !== 'ollama' && selectedProvider.value.id !== 'player2'
})

const needsBaseUrl = computed(() => {
  if (!selectedProvider.value)
    return false
  const lockedBaseUrlProviders = ['cloudflare-workers-ai', 'neko-teaparty']
  return !lockedBaseUrlProviders.includes(selectedProvider.value.id)
})

const canSave = computed(() => {
  if (!selectedProvider.value)
    return false
  if (needsApiKey.value && !apiKey.value.trim())
    return false
  if (needsBaseUrl.value && !baseUrl.value.trim())
    return false
  if (selectedProvider.value.id === 'cloudflare-workers-ai' && !accountId.value.trim())
    return false
  if (!activeModel.value)
    return false
  return isValid.value
})

const onboardingStatus = computed<ProviderStatus>(() => {
  const provider = selectedProvider.value
  // 对支持 listModels 的 provider：合并 store 的拉取状态，避免本地 valid 但 store 拉模型失败
  if (provider && provider.capabilities.listModels) {
    const storeLoading = providersStore.isLoadingModels[provider.id]
    const storeErr = providersStore.modelLoadError[provider.id]
    const storeModels = providersStore.availableModels[provider.id]
    if (isValidating.value > 0 || storeLoading)
      return { kind: 'validating' }
    if (storeErr)
      return { kind: 'invalid', error: storeErr }
    if (validationMessage.value)
      return { kind: 'invalid', error: serializeProviderError(validationMessage.value) }
    if (isValid.value && storeModels && storeModels.length > 0)
      return { kind: 'valid' }
    return { kind: 'idle' }
  }
  if (isValidating.value > 0)
    return { kind: 'validating' }
  if (isValid.value)
    return { kind: 'valid' }
  if (validationMessage.value)
    return { kind: 'invalid', error: serializeProviderError(validationMessage.value) }
  return { kind: 'idle' }
})

function selectProvider(provider: typeof popularProviders.value[0]) {
  if (selectedProviderId.value === provider.id)
    return
  selectedProviderId.value = provider.id
  const defaultOptions = provider.defaultOptions?.() || {}
  baseUrl.value = (defaultOptions as any)?.baseUrl || ''
  apiKey.value = ''
  accountId.value = ''
  isValid.value = false
  validationMessage.value = ''
}

function getApiKeyPlaceholder(_providerId: string): string {
  const placeholders: Record<string, string> = {
    'openai': 'sk-...',
    'anthropic': 'sk-ant-...',
    'google-generative-ai': 'GEMINI_API_KEY',
    'openrouter-ai': 'sk-or-...',
    'deepseek': 'sk-...',
    'xai': 'xai-...',
    'together-ai': 'togetherapi-...',
    'mistral-ai': 'mis-...',
    'moonshot-ai': 'ms-...',
    'modelscope': 'ms-...',
    'fireworks-ai': 'fw-...',
    'featherless-ai': 'fw-...',
    'novita-ai': 'nvt-...',
  }
  return placeholders[_providerId] || 'API Key'
}

function getBaseUrlPlaceholder(_providerId: string): string {
  const defaultOptions = selectedProvider.value?.defaultOptions?.() || {}
  return (defaultOptions as any)?.baseUrl || 'https://api.example.com/v1/'
}

async function validateConfiguration() {
  if (!selectedProvider.value)
    return
  isValidating.value++
  const startValidationTimestamp = performance.now()
  let finalValidationMessage = ''
  try {
    const config: Record<string, unknown> = {}
    if (needsApiKey.value)
      config.apiKey = apiKey.value.trim()
    if (needsBaseUrl.value)
      config.baseUrl = baseUrl.value.trim()
    if (selectedProvider.value.id === 'cloudflare-workers-ai')
      config.accountId = accountId.value.trim()
    const metadata = providersStore.getProviderMetadata(selectedProvider.value.id)
    const validationResult = await metadata.validators.validateProviderConfig(config)
    isValid.value = validationResult.valid
    if (!isValid.value) {
      finalValidationMessage = validationResult.reason
    }
  }
  catch (error) {
    isValid.value = false
    finalValidationMessage = t('settings.dialogs.onboarding.validationError', {
      error: error instanceof Error ? error.message : String(error),
    })
  }
  finally {
    setTimeout(() => {
      isValidating.value--
      validationMessage.value = finalValidationMessage
    }, debounceTime - (performance.now() - startValidationTimestamp))
  }
}

const debouncedValidateConfiguration = useDebounceFn(() => {
  if (!selectedProvider.value)
    return
  if (needsApiKey.value && !apiKey.value.trim())
    return
  if (needsBaseUrl.value && !baseUrl.value.trim())
    return
  if (selectedProvider.value.id === 'cloudflare-workers-ai' && !accountId.value.trim())
    return
  validateConfiguration()
}, debounceTime)

watch([apiKey, baseUrl, accountId], () => {
  if (selectedProvider.value && (apiKey.value || baseUrl.value || accountId.value)) {
    debouncedValidateConfiguration()
  }
}, { deep: true })

function handlePreviousStep() {
  if (step.value > 1) {
    direction.value = 'previous'
    step.value--
  }
}

function handleNextStep() {
  if (step.value < TOTAL_STEPS) {
    direction.value = 'next'
    step.value++
  }
  else {
    handleSave()
  }
}

function handleSkipStep() {
  if (step.value >= TOTAL_STEPS) {
    handleSave()
    return
  }
  direction.value = 'next'
  step.value++
}

async function handlePersonaComplete(data: { name: string, avatar: string, gender: 'male' | 'female' | 'unspecified', birthday: string, persona: string }) {
  if (data.name) {
    const persona = userPersonaStore.createPersona({
      name: data.name,
      avatar: data.avatar || '/USER.jpg',
      gender: data.gender,
      birthday: data.birthday || undefined,
      persona: data.persona || undefined,
    })
    userPersonaStore.setDefaultPersona(persona.id)
  }
  handleNextStep()
}

async function handleFinishProviderConfiguration() {
  if (!selectedProvider.value)
    return
  const config: Record<string, unknown> = {}
  if (needsApiKey.value)
    config.apiKey = apiKey.value.trim()
  if (needsBaseUrl.value)
    config.baseUrl = baseUrl.value.trim()
  if (selectedProvider.value.id === 'cloudflare-workers-ai')
    config.accountId = accountId.value.trim()
  providers.value[selectedProvider.value.id] = {
    ...providers.value[selectedProvider.value.id],
    ...config,
  }
  await providersStore.toggleProvider(selectedProvider.value.id, true)
  await nextTick()
  try {
    await consciousnessStore.loadModelsForProvider(selectedProvider.value.id)
  }
  catch (err) {
    console.error('error', err)
  }
  handleNextStep()
}

function handleModelAndLive2dComplete() {
  if (useLive2d.value === 'no') {
    settingsStore.stageModelSelected = DISPLAY_MODEL_NONE
    settingsStore.stageModelVisible = false
    settingsStore.mobileChatHistoryMaxHeight = 90
  }
  handleNextStep()
}

async function handleSave() {
  emit('configured')
}

onMounted(() => {
  const nekoTeaParty = popularProviders.value.find(p => p.id === 'neko-teaparty')
  if (nekoTeaParty) {
    selectedProviderId.value = 'neko-teaparty'
    selectProvider(nekoTeaParty)
  }
  else if (popularProviders.value.length > 0) {
    selectedProviderId.value = popularProviders.value[0].id
    selectProvider(popularProviders.value[0])
  }
})
</script>

<template>
  <div class="h-full w-full flex flex-col overflow-hidden">
    <!-- 进度指示器 -->
    <div class="mb-1 flex flex-shrink-0 flex-col items-center gap-1">
      <div class="w-full flex items-center justify-center gap-3">
        <div class="flex items-center justify-center gap-1.5">
          <div
            v-for="i in TOTAL_STEPS"
            :key="i"
            class="h-1.5 rounded-full transition-all duration-300"
            :class="i === step ? 'w-6 bg-primary-500' : i < step ? 'w-1.5 bg-primary-300 dark:bg-primary-700' : 'w-1.5 bg-neutral-200 dark:bg-neutral-700'"
          />
        </div>
        <Button
          size="sm"
          variant="secondary-muted"
          :label="t('settings.dialogs.onboarding.skipAll')"
          @click="emit('skipped')"
        />
      </div>
      <div class="text-xs text-neutral-400 dark:text-neutral-500">
        {{ step }} / {{ TOTAL_STEPS }} · {{ t(`settings.dialogs.onboarding.stepNames.step${step}`) }}
      </div>
    </div>

    <Transition :name="direction === 'next' ? 'slide-next' : 'slide-prev'" mode="out-in" @after-enter="onAfterEnter" @before-leave="onBeforeLeave">
      <!-- Step 1: PWA 安装引导（智能跳过） -->
      <div v-if="step === 1" :key="1" class="flex flex-1 flex-col">
        <StepInstallPwa @next="handleNextStep" @skip="handleNextStep" />
      </div>

      <!-- Step 2: 欢迎页 -->
      <div v-else-if="step === 2" :key="2" class="flex flex-1 flex-col">
        <template v-if="contentReady">
          <div class="mb-2 flex flex-1 flex-col justify-center text-center md:mb-8">
            <div
              v-motion
              :initial="{ opacity: 0, scale: 0.7 }"
              :enter="{ opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 200, damping: 20 } }"
              class="mb-1 flex justify-center md:mb-4 lg:pt-16 md:pt-8"
            >
              <img :src="onboardingLogo" max-h="50" h-auto w-auto object-contain alt="MeowChat Logo">
            </div>
            <h2
              v-motion
              :initial="{ opacity: 0, y: 20 }"
              :enter="{ opacity: 1, y: 0 }"
              :duration="400"
              :delay="150"
              class="mb-0 text-3xl text-neutral-800 md:mb-2 dark:text-neutral-100"
            >
              {{ t('settings.dialogs.onboarding.title') }}
            </h2>
            <p
              v-motion
              :initial="{ opacity: 0, y: 16 }"
              :enter="{ opacity: 1, y: 0 }"
              :duration="400"
              :delay="260"
              class="text-sm text-neutral-600 md:text-lg dark:text-neutral-400"
            >
              {{ t('settings.dialogs.onboarding.description') }}
            </p>
          </div>
          <Button
            v-motion
            :initial="{ opacity: 0, y: 10 }"
            :enter="{ opacity: 1, y: 0 }"
            :duration="300"
            :delay="380"
            :label="t('settings.dialogs.onboarding.start')"
            @click="handleNextStep"
          />
        </template>
      </div>

      <!-- Step 3: 用户人格设置 -->
      <div v-else-if="step === 3" :key="3" class="min-h-0 flex flex-1 flex-col">
        <div class="sticky top-0 z-100 mb-2 flex flex-shrink-0 items-center gap-2">
          <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
            <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
          </button>
          <div class="h-5 w-5" />
        </div>
        <StepPersona class="min-h-0 flex-1" @next="handlePersonaComplete" @skip="handleSkipStep" />
      </div>

      <!-- Step 4: Provider Selection -->
      <div v-else-if="step === 4" :key="4" class="min-h-0 flex flex-1 flex-col gap-4">
        <template v-if="contentReady">
          <div
            v-motion
            :initial="{ opacity: 0, y: 16 }"
            :enter="{ opacity: 1, y: 0 }"
            :duration="380"
            class="sticky top-0 z-100 flex flex-shrink-0 items-center gap-2"
          >
            <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
              <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
            </button>
            <h2 class="flex-1 text-center text-xl text-neutral-800 md:text-left md:text-2xl dark:text-neutral-100">
              {{ t('settings.dialogs.onboarding.selectProvider') }}
            </h2>
            <div class="h-5 w-5" />
          </div>
          <div
            v-motion
            :initial="{ opacity: 0, y: 24 }"
            :enter="{ opacity: 1, y: 0 }"
            :duration="420"
            :delay="120"
            class="flex-1 overflow-y-auto"
          >
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <RadioCardDetail
                v-for="(provider, index) in popularProviders"
                :id="provider.id"
                :key="provider.id"
                v-model="selectedProviderId"
                v-motion
                :initial="{ opacity: 0, y: 12 }"
                :enter="{ opacity: 1, y: 0 }"
                :duration="300"
                :delay="120 + Math.min(index * 40, 200)"
                name="provider-selection"
                :value="provider.id"
                :title="provider.localizedName || provider.id"
                :description="provider.localizedDescription || ''"
                @click="selectProvider(provider)"
              />
            </div>
          </div>
          <Button
            v-motion
            :initial="{ opacity: 0 }"
            :enter="{ opacity: 1 }"
            :duration="300"
            :delay="260"
            :label="t('settings.dialogs.onboarding.next')"
            :disabled="!selectedProviderId"
            @click="handleNextStep"
          />
        </template>
      </div>

      <!-- Step 5: Configuration Form -->
      <div v-else-if="step === 5 && selectedProvider" :key="5" class="min-h-0 flex flex-1 flex-col gap-4">
        <template v-if="contentReady">
          <div
            v-motion
            :initial="{ opacity: 0, y: 12 }"
            :enter="{ opacity: 1, y: 0 }"
            :duration="350"
            class="sticky top-0 z-100 flex flex-shrink-0 items-center gap-2"
          >
            <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
              <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
            </button>
            <h2 class="flex-1 text-center text-xl text-neutral-800 md:text-left md:text-2xl dark:text-neutral-100">
              {{ t('settings.dialogs.onboarding.configureProvider', { provider: selectedProvider!.localizedName }) }}
            </h2>
            <div class="h-5 w-5" />
          </div>
          <div v-if="selectedProvider" class="flex-1 overflow-y-auto space-y-4">
            <div
              v-motion
              :initial="{ opacity: 0, y: 16 }"
              :enter="{ opacity: 1, y: 0 }"
              :duration="350"
              :delay="100"
            >
              <Callout :label="t('settings.dialogs.onboarding.securityNoticeTitle')" theme="violet">
                <div>
                  {{ t('settings.dialogs.onboarding.securityNoticeContent') }}
                </div>
              </Callout>
            </div>
            <div
              v-motion
              :initial="{ opacity: 0, y: 16 }"
              :enter="{ opacity: 1, y: 0 }"
              :duration="350"
              :delay="180"
              class="space-y-4"
            >
              <div v-if="needsApiKey">
                <FieldInput
                  v-model="apiKey"
                  :placeholder="getApiKeyPlaceholder(selectedProvider.id)"
                  type="password"
                  :label="t('settings.dialogs.onboarding.apiKeyLabel')"
                  :description="t('settings.dialogs.onboarding.apiKeyDescription')"
                  required
                />
                <div v-if="selectedProvider.id === 'neko-teaparty'" class="mt-2 text-sm">
                  <a
                    href="https://api.nktp.top"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary-600 underline decoration-dashed dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    {{ t('settings.dialogs.onboarding.getApiKey') }}
                  </a>
                </div>
              </div>
              <div v-if="needsBaseUrl">
                <FieldInput
                  v-model="baseUrl"
                  :placeholder="getBaseUrlPlaceholder(selectedProvider.id)"
                  type="text"
                  :label="t('settings.dialogs.onboarding.baseUrlLabel')"
                  :description="t('settings.dialogs.onboarding.baseUrlDescription')"
                />
              </div>
              <div v-if="selectedProvider.id === 'cloudflare-workers-ai'">
                <ProviderAccountIdInput v-model="accountId" />
              </div>
            </div>
            <ProviderStatusPanel :status="onboardingStatus" />
          </div>
          <Button
            v-motion
            :initial="{ opacity: 0 }"
            :enter="{ opacity: 1 }"
            :duration="280"
            :delay="280"
            :label="t('settings.dialogs.onboarding.next')"
            :loading="isLoadingActiveProviderModels || isValidating > 0"
            :disabled="!selectedProviderId || (needsApiKey && apiKey.trim().length === 0) || !isValid"
            @click="handleFinishProviderConfiguration"
          />
        </template>
      </div>

      <!-- Step 6: Model Selection + Live2D 偏好 -->
      <div v-else-if="step === 6 && selectedProvider" :key="6" class="min-h-0 flex flex-1 flex-col gap-4">
        <template v-if="contentReady">
          <div
            v-motion
            :initial="{ opacity: 0, y: 16 }"
            :enter="{ opacity: 1, y: 0 }"
            :duration="380"
            class="sticky top-0 z-100 flex flex-shrink-0 items-center gap-2"
          >
            <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
              <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
            </button>
            <h2 class="flex-1 text-center text-xl text-neutral-800 md:text-left md:text-2xl dark:text-neutral-100">
              {{ t('settings.dialogs.onboarding.select-model') }}
            </h2>
            <div class="h-5 w-5" />
          </div>

          <div class="flex-1 overflow-y-auto space-y-4">
            <!-- AI 模型选择 -->
            <div
              v-motion
              :initial="{ opacity: 0, y: 20 }"
              :enter="{ opacity: 1, y: 0 }"
              :duration="400"
              :delay="120"
            >
              <RadioCardManySelect
                v-if="providerModels.length > 0"
                v-model="activeModel"
                v-model:search-query="modelSearchQuery"
                :items="[...providerModels].sort((a, b) => a.id === activeModel ? -1 : b.id === activeModel ? 1 : 0)"
                :searchable="true"
                :search-placeholder="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.search_placeholder')"
                :search-no-results-title="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.no_search_results')"
                :search-no-results-description="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.no_search_results_description', { query: modelSearchQuery })"
                :search-results-text="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.search_results', { count: '{count}', total: '{total}' })"
                :custom-input-placeholder="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.custom_model_placeholder')"
                :expand-button-text="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.expand')"
                :collapse-button-text="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.collapse')"
                list-class="max-h-[calc(100dvh-26rem)] sm:max-h-60"
              />
              <Alert v-else type="error">
                <template #title>
                  {{ t('settings.dialogs.onboarding.no-models') }}
                </template>
                <template #content>
                  <div class="whitespace-pre-wrap break-all">
                    {{ t('settings.dialogs.onboarding.no-models-help') }}
                  </div>
                </template>
              </Alert>
            </div>
            <div
              v-motion
              :initial="{ opacity: 0, y: 20 }"
              :enter="{ opacity: 1, y: 0 }"
              :duration="380"
              :delay="220"
            >
              <h3 class="mb-2 text-base text-neutral-800 font-medium dark:text-neutral-100">
                {{ t('settings.dialogs.onboarding.displayModel.title') }}
              </h3>
              <p class="mb-2 text-xs text-neutral-500 dark:text-neutral-400">
                {{ t('settings.dialogs.onboarding.displayModel.description') }}
              </p>
              <div class="grid grid-cols-2 gap-3">
                <RadioCardDetail
                  id="live2d-yes"
                  v-model="useLive2d"
                  name="live2d-pref"
                  value="yes"
                  :title="t('settings.dialogs.onboarding.displayModel.useLive2d')"
                  :description="t('settings.dialogs.onboarding.displayModel.useLive2dDesc')"
                  :show-expand-collapse="false"
                />
                <RadioCardDetail
                  id="live2d-no"
                  v-model="useLive2d"
                  name="live2d-pref"
                  value="no"
                  :title="t('settings.dialogs.onboarding.displayModel.noModel')"
                  :description="t('settings.dialogs.onboarding.displayModel.noModelDesc')"
                  :show-expand-collapse="false"
                />
              </div>
            </div>
          </div>

          <Button
            v-motion
            :initial="{ opacity: 0 }"
            :enter="{ opacity: 1 }"
            :duration="300"
            :delay="340"
            variant="primary"
            :disabled="!canSave"
            :label="t('settings.dialogs.onboarding.next')"
            @click="handleModelAndLive2dComplete"
          />
        </template>
      </div>

      <!-- Step 7: 外观个性化 -->
      <div v-else-if="step === 7" :key="7" class="min-h-0 flex flex-1 flex-col">
        <div class="sticky top-0 z-100 mb-2 flex flex-shrink-0 items-center gap-2">
          <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
            <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
          </button>
          <div class="h-5 w-5" />
        </div>
        <StepAppearance class="min-h-0 flex-1" @next="handleNextStep" @skip="handleSkipStep" />
      </div>

      <!-- Step 8: 初始关系 -->
      <div v-else-if="step === 8" :key="8" class="min-h-0 flex flex-1 flex-col">
        <div class="sticky top-0 z-100 mb-2 flex flex-shrink-0 items-center gap-2">
          <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
            <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
          </button>
          <div class="h-5 w-5" />
        </div>
        <StepRelationship class="min-h-0 flex-1" @next="handleNextStep" @skip="handleSkipStep" />
      </div>

      <!-- Step 9: 广场 -->
      <div v-else-if="step === 9" :key="9" class="min-h-0 flex flex-1 flex-col">
        <div class="sticky top-0 z-100 mb-2 flex flex-shrink-0 items-center gap-2">
          <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
            <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
          </button>
          <div class="h-5 w-5" />
        </div>
        <StepMarketplace class="min-h-0 flex-1" @next="handleNextStep" @skip="handleNextStep" />
      </div>

      <!-- Step 10: 百度网盘备份引导 -->
      <div v-else-if="step === 10" :key="10" class="min-h-0 flex flex-1 flex-col">
        <div class="sticky top-0 z-100 mb-2 flex flex-shrink-0 items-center gap-2">
          <button class="min-h-11 min-w-11 flex items-center justify-center rounded-full outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800" aria-label="返回上一步" @click="handlePreviousStep">
            <div class="i-solar:alt-arrow-left-line-duotone h-5 w-5" />
          </button>
          <div class="h-5 w-5" />
        </div>
        <StepBaiduSync class="min-h-0 flex-1" @finish="handleSave" @skip="handleSave" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.slide-next-enter-active,
.slide-next-leave-active {
 transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
}

.slide-next-enter-from {
 transform: translateX(100%);
 opacity: 0;
}

.slide-next-enter-to {
 transform: translateX(0);
 opacity: 1;
}

.slide-next-leave-from {
 transform: translateX(0);
 opacity: 1;
}

.slide-next-leave-to {
 transform: translateX(-100%);
 opacity: 0;
}

.slide-prev-enter-active,
.slide-prev-leave-active {
 transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
}

.slide-prev-enter-from {
 transform: translateX(-100%);
 opacity: 0;
}

.slide-prev-enter-to {
 transform: translateX(0);
 opacity: 1;
}

.slide-prev-leave-from {
 transform: translateX(0);
 opacity: 1;
}

.slide-prev-leave-to {
 transform: translateX(100%);
 opacity: 0;
}
</style>
